/**
 * <Badge> — small status/category indicator.
 *
 * Semantic-soft palette (UI-SPEC §Color): pairs each tone with its
 * `-soft` background + base text color for AA-safe contrast. Radius is
 * `rounded-sm` (4px per D-03). Typography is `text-label` (13px + muted
 * letter-spacing). No hex literals, no raw color scales.
 *
 * `variant="ai"` is the one look for what the AI did: the AI tone and a
 * leading ✦, always. Colour is never the only signal, so its text must say
 * AI ("AI", "Identified by AI").
 */
import * as React from "react";
import { Sparkles } from "lucide-react";
import { cn, cva, type VariantProps } from "../../utils";
import { Icon } from "../Icon";

export const badgeVariants = cva(
  cn("inline-flex items-center gap-xs rounded-sm text-label", "whitespace-nowrap"),
  {
    variants: {
      variant: {
        neutral: "bg-surface-sunken text-default border border-subtle",
        // `-ink` text: the tone's AA shade for its own tint, in both themes.
        primary: "bg-primary-soft text-primary-ink",
        success: "bg-success-soft text-success-ink",
        warning: "bg-warning-soft text-warning-ink",
        danger: "bg-danger-soft text-danger-ink",
        info: "bg-info-soft text-info-ink",
        ai: "bg-ai-soft text-ai-ink",
      },
      size: {
        // `sm` keeps the same xs vertical padding but tightens horizontal
        // to the xs step — stays on the 4px spacing scale per UI-SPEC.
        sm: "px-xs py-xs text-label",
        md: "px-sm py-xs text-label",
      },
    },
    defaultVariants: { variant: "neutral", size: "md" },
  },
);

export type BadgeProps = React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>;

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { className, variant, size, children, ...rest },
  ref,
) {
  return (
    <span ref={ref} className={cn(badgeVariants({ variant, size }), className)} {...rest}>
      {variant === "ai" ? <Icon as={Sparkles} size={12} strokeWidth={2} /> : null}
      {children}
    </span>
  );
});
